import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import TrainingPage from "@/pages/TrainingPage";
import { Route, Switch, Redirect, useSearch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import ClaudeRoute from "./pages/ClaudeRoute";
import Claude8Wins from "./pages/Claude8Wins";
import OpenVideoRoute from "./pages/OpenVideoRoute";
import OpenVideoHooks from "./pages/OpenVideoHooks";
import OpenVideoReferenceCard from "./pages/OpenVideoReferenceCard";
import OpenVideo30DayCalendar from "./pages/OpenVideo30DayCalendar";
import ResourceCenter from "./pages/ResourceCenter";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
// AI Tools route pages
import ChatGPTRoute from "./pages/ChatGPTRoute";
import PerplexityRoute from "./pages/PerplexityRoute";
import PerplexityEightWays from "./pages/PerplexityEightWays";
import ManusEightWays from "./pages/ManusEightWays";
import ManusQuickWinsPage from "./pages/ManusQuickWinsPage";
import GeminiEightWays from "./pages/GeminiEightWays";
import GeminiQuickWinsPage from "./pages/GeminiQuickWinsPage";
import PerplexityQuickWinsPage from "./pages/PerplexityQuickWinsPage";
import ManusAIRoute from "./pages/ManusAIRoute";
import NotebookLMRoute from "./pages/NotebookLMRoute";
import CanvaAIRoute from "./pages/CanvaAIRoute";
import CanvaBasics from "./pages/CanvaBasics";
import GeminiRoute from "./pages/GeminiRoute";
import GammaRoute from "./pages/GammaRoute";
import Base44Route from "./pages/Base44Route";
import Base44EightWays from "./pages/Base44EightWays";
import LovableRoute from "./pages/LovableRoute";
import LovableEightWays from "./pages/LovableEightWays";
import GammaEightWays from "./pages/GammaEightWays";
import CastmagicRoute from "./pages/CastmagicRoute";
import CastmagicBrandVoice from "./pages/CastmagicBrandVoice";
import OpusClipRoute from "./pages/OpusClipRoute";
import OpusClipCheatSheet from "./pages/OpusClipCheatSheet";
import AgentOpusRoute from "./pages/AgentOpusRoute";
import LittlebirdRoute from "./pages/LittlebirdRoute";
import LittlebirdEightWays from "./pages/LittlebirdEightWays";
import LittlebirdScenarioFinder from "./pages/LittlebirdScenarioFinder";
import LittlebirdOptin from "./pages/LittlebirdOptin";
import LittlebirdQuickWinsPage from "./pages/LittlebirdQuickWinsPage";
import NotebookLMEightWays from "./pages/NotebookLMEightWays";
import ClaudeQuickWinsPage from "./pages/ClaudeQuickWinsPage";
import ClaudeQuickWinsProtected from "@/pages/ClaudeQuickWinsProtected";
import ClaudeQuickWinsDelivery from "@/pages/ClaudeQuickWinsDelivery";
import NotebookLMQuickWinsPage from "./pages/NotebookLMQuickWinsPage";
import NotebookLMOptin from "./pages/NotebookLMOptin";
// Resource Center route pages
import GoHighLevelRoute from "./pages/GoHighLevelRoute";
import { useAuth } from "./core/hooks/useAuth";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  AuthModalProvider,
  useAuthModal,
  type AuthModalMode,
} from "./contexts/AuthModalContext";
import { safeRedirect } from "@/lib/safeRedirect";
import AuthModal from "./components/AuthModal";

import MyStack from "./pages/MyStack";
import PartnerDashboard from "./pages/PartnerDashboard";
import AdminPartners from "./pages/AdminPartners";
import Admin from "./pages/Admin";
import PromptLibrary from "./pages/PromptLibrary";
import PromptPage from "./pages/PromptPage";
import { useAdoptLocalState } from "@/hooks/useStack";
/**
 * Gate a route behind an account.
 *
 * This used to wrap every route in the app, including the home page, so a
 * signed-out visitor saw nothing at all. The directory, the quiz and the
 * per-tool overview pages are public now; what stays behind this wrapper is the
 * depth: the interactive guides that are the actual reason to create an
 * account. Opening the directory is what makes the site indexable and what lets
 * someone judge it before being asked for an email.
 *
 * The redirect target is still /auth, which now renders the directory with the
 * signup dialog open over it rather than a separate page.
 */
/**
 * An email opt-in lander, skipped for people who already have an account.
 *
 * The landers (/littlebird-quickwins, /gemini-notebook-quickwins,
 * /claude-quick-wins) are a separate capture system from the site's own
 * accounts: they ask for a first name and email and mail the guide out. That
 * is the right ask for someone arriving cold from an ad or an email campaign.
 *
 * It is the wrong ask for someone who is signed in. They have already given us
 * their email, and the guide is already readable on this site. Asking again
 * reads as though the account they made did not count for anything, and it
 * sends them to their inbox to get something that was one click away.
 *
 * So: signed in goes straight to the guide, signed out sees the lander. The
 * lander's own post-submit behaviour is untouched.
 */
function OptinRoute({
  component: Component,
  guide,
}: {
  component: React.ComponentType;
  guide: string;
}) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  if (user) return <Redirect to={guide} />;

  return <Component />;
}

/**
 * Gate a route behind the admin role.
 *
 * This is not the security boundary and should not be read as one. Every
 * `admin.*` procedure is on `adminProcedure`, which is what actually refuses;
 * this only decides what gets rendered, and its job is to send the wrong person
 * somewhere sensible rather than to a screen of failed requests.
 *
 * A signed-out visitor goes to the login page, because signing in may well fix
 * it. Someone signed in without the role goes to the home page rather than to
 * a "not allowed" screen: the panel is not something they were promised and
 * missed out on, and telling them it exists is the only thing such a screen
 * would achieve.
 */
function AdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) setLocation("/login?returnTo=%2Fadmin");
    else if (user.role !== "admin") setLocation("/");
  }, [loading, user, setLocation]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  return <Component />;
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, loading } = useAuth();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      /* CHANGED Aug 2026: this sent people to /auth, which renders the
         directory with the sign-in dialog over it. That is the right screen
         for someone already browsing and the wrong one for someone who just
         tried to open a gated guide: the page they asked for is replaced by
         an unrelated one, and dismissing the dialog — easy to do by accident —
         strands them there with the destination silently gone.

         /login is the dedicated sign-in screen, and it is already where
         AdminRoute, const.ts's getLoginUrl and the server's getStableLoginUrl
         point. This was the last caller still on the old path, and the one
         covering every gated guide, so it was also the highest-traffic one. */
      const returnTo = encodeURIComponent(location);
      setLocation(`/login?returnTo=${returnTo}`);
    }
  }, [loading, user, setLocation, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <Component />;
}

/**
 * /auth and its legacy aliases.
 *
 * CORRECTED Aug 2026: this used to say "there is no dedicated login page any
 * more", which stopped being true when /login was added. /login is now the
 * screen for anyone following a link that said "log in" — every new caller
 * points there, and `auth.loginUrl` returns it.
 *
 * These paths stay exactly as they are, rendering the directory with the auth
 * dialog over it, because they are live in GoHighLevel welcome emails that
 * have already been sent and cannot be recalled. Nothing new should point
 * here, but nothing already pointing here may break.
 */
function AuthEntry({ mode }: { mode: AuthModalMode }) {
  const { open } = useAuthModal();
  const searchString = useSearch();
  const { user, loading } = useAuth();

  /* Read once on mount. The destination is a property of the link that was
     clicked, and re-reading it as the query string changes would move the
     target out from under a half-filled form. Undefined when the link carried
     no destination, which the dialog treats as "stay where you are" — passing
     "/" instead would turn a dismissable dialog into a page navigation. */
  const [destination] = useState(() => {
    const raw = new URLSearchParams(searchString).get("returnTo");
    return raw ? safeRedirect(raw) : undefined;
  });

  /* The decision is made once, but not until `loading` resolves, so this
     cannot be an empty dependency list guarded by nothing. */
  const decided = useRef(false);

  useEffect(() => {
    if (loading || decided.current) return;
    decided.current = true;

    /* BUG FIX, Aug 2026: this opened the dialog unconditionally, without ever
       looking at whether the visitor was already signed in.

       That is wrong everywhere, but it was worst for the cross-app flow. The
       session cookie is scoped to .yoursmartstack.com, so someone signed in
       here is already signed in for Quick Wins. When Quick Wins bounced them
       over to confirm, they were shown a login form for the account they were
       currently using — and the destination they were sent to collect was
       sitting unused in the query string. From their side that is "it keeps
       asking me to log in and never lets me through". */
    if (user) {
      if (destination) window.location.href = destination;
      return;
    }

    open({ mode, returnTo: destination });
  }, [loading, user, destination, open, mode]);

  return <Home />;
}

function Router() {
  /* Hands anything saved anonymously to the account, once, on sign-in. It
     lives here rather than in a page so it fires no matter which route
     someone signs in from. */
  useAdoptLocalState();

  return (
    <Switch>
      {/* The dedicated sign-in page, for people arriving from an email, a
          bookmark or a shared link rather than from the directory. */}
      <Route path={"/login"} component={Login} />

      {/* Auth entry points: the directory with the dialog open over it.
          Still right for someone already browsing, and still live in every
          welcome email sent before Aug 2026. */}
      {/* FIXED 2 Sep 2026: all three of these opened the dialog on the Sign
          In tab, whatever the link that reached them said. The storefront has
          always separated the two - "Create free account" and "Start Free"
          point at /auth, "Log In" points at /auth/login - so someone taking
          the storefront's primary call to action was handed a form asking for
          a password they had not chosen yet. The path carries the intent;
          these now read it. */}
      <Route path={"/auth"}>{() => <AuthEntry mode="signup" />}</Route>
      {/* Legacy paths, kept working because they exist in sent emails */}
      <Route path={"/auth/landing"}>{() => <AuthEntry mode="signup" />}</Route>
      <Route path={"/auth/login"}>{() => <AuthEntry mode="login" />}</Route>
      <Route path={"/auth/forgot-password"} component={ForgotPassword} />
      <Route path={"/auth/reset-password"} component={ResetPassword} />

      {/* Public: the directory itself */}
      <Route path={"/"} component={Home} />
      {/* The Partner dashboard is gated on having an account, not on being an
          admin: Caren and Daniel are Partners, and so is everybody they
          approve. `partner.me` refuses a signed-in customer who has no Partner
          record, with a message that says what to do about it.

          Managing Partners and paying them is a different matter and is on the
          admin gate below.

          (Kept clear of the /admin block above on purpose. routes.test.ts
          reads a flat 300-character slice after each route to check what gates
          it, so a mention of a different gate too close by fails a test that
          is right about the thing it is guarding.) */}
      <Route path={"/partner"}>{() => <ProtectedRoute component={PartnerDashboard} />}</Route>
      <Route path={"/admin/partners"}>{() => <AdminRoute component={AdminPartners} />}</Route>

      {/* The admin panel. Guarded here for the redirect; guarded for real on
          the server, where every admin.* procedure sits on adminProcedure. */}
      <Route path={"/admin"}>
        <AdminRoute component={Admin} />
      </Route>

      {/* Resource Center index */}
      <Route path={"/my-stack"} component={MyStack} />
      <Route path={"/resources"} component={ResourceCenter} />

      {/* The Prompt Library. Public routes on purpose, with the gate on the
          server: anyone may browse the cards, and prompts.get sends a prompt's
          text only to a Smart Stack + member or an admin. A ProtectedRoute
          here would hide the library from the very people it is meant to
          persuade, and would not be a paywall anyway. */}
      <Route path={"/prompts"} component={PromptLibrary} />
      <Route path={"/prompts/:slug"} component={PromptPage} />

      {/* Public ChatGPT HTML pages */}
      <Route path="/chatgpt-quick-wins">
        {() => {
          window.location.href = '/chatgpt-quick-wins.html';
          return null;
        }}
      </Route>
      <Route path="/chatgpt-basics">
        {() => {
          window.location.href = '/chatgpt-basics.html';
          return null;
        }}
      </Route>

      {/* /resources/claude/quick-wins-full was registered twice: unprotected
          here, and wrapped in ProtectedRoute further down. Switch takes the
          first match, so the protected version below was dead code and the
          guide opened for anyone. That is why Claude's Quick Wins step went
          straight in while Gemini Notebook's asked for an account. The
          duplicate is removed; the gated route below is the live one. */}
      <Route path="/claude-quick-wins-delivery" component={ClaudeQuickWinsDelivery} />
      {/* Vanity URL for Claude Quick Wins */}
      <Route path={"/claude-quick-wins"}>
        <OptinRoute component={ClaudeQuickWinsPage} guide="/resources/claude/quick-wins-full" />
      </Route>

      {/* Claude resources */}
      <Route path={"/resources/claude"} component={ClaudeRoute} />
      <Route path={"/resources/claude/8-wins"}>
        <ProtectedRoute component={Claude8Wins} />
      </Route>
      {/* Protected Claude Quick Wins: full version for logged-in users */}
      <Route path={"/resources/claude/quick-wins-full"}>
        <ProtectedRoute component={ClaudeQuickWinsProtected} />
      </Route>

      {/* The video trainings, brought onto this site from
          carenglasser.com so the most committed step on a tool path
          does not hand the visitor to another domain. */}
      <Route path={"/trainings/:slug"} component={TrainingPage} />

      {/* Open.Video resources */}
      <Route path={"/resources/open-video"} component={OpenVideoRoute} />
      <Route path={"/resources/open-video/hook-library"}>
        <ProtectedRoute component={OpenVideoHooks} />
      </Route>
      <Route path={"/resources/open-video/reference-card"}>
        <ProtectedRoute component={OpenVideoReferenceCard} />
      </Route>
      <Route path={"/resources/open-video/30-day-calendar"}>
        <ProtectedRoute component={OpenVideo30DayCalendar} />
      </Route>

      {/* AI Tools route pages */}
      <Route path={"/resources/chatgpt"} component={ChatGPTRoute} />
      <Route path={"/resources/perplexity"} component={PerplexityRoute} />
      {/* Gated to match every other tool's pair of guides. See the note on the
          Littlebird pair below: these are the same kind of thing at the same
          depth in the same path, so asking for an account on one and not the
          other is the inconsistency, not the gate. */}
      <Route path={"/resources/perplexity/8-ways"}>
        <ProtectedRoute component={PerplexityEightWays} />
      </Route>
      <Route path={"/resources/perplexity/quick-wins"}>
        <ProtectedRoute component={PerplexityQuickWinsPage} />
      </Route>
      <Route path={"/resources/manus"} component={ManusAIRoute} />
      <Route path={"/resources/manus/8-ways"}>
        <ProtectedRoute component={ManusEightWays} />
      </Route>
      <Route path={"/resources/manus/quick-wins"}>
        <ProtectedRoute component={ManusQuickWinsPage} />
      </Route>
      <Route path={"/resources/gemini-notebook"} component={NotebookLMRoute} />
      <Route path={"/resources/canva"} component={CanvaAIRoute} />
      <Route path={"/resources/canva/basics"}>
        <ProtectedRoute component={CanvaBasics} />
      </Route>
      <Route path={"/resources/gemini"} component={GeminiRoute} />
      {/* Gemini, not Gemini Notebook. Two Google products with adjacent names
          and adjacent routes; the pair below at /resources/gemini-notebook/*
          is a different set of guides about a different tool. */}
      <Route path={"/resources/gemini/8-ways"}>
        <ProtectedRoute component={GeminiEightWays} />
      </Route>
      <Route path={"/resources/gemini/quick-wins"}>
        <ProtectedRoute component={GeminiQuickWinsPage} />
      </Route>
      <Route path={"/resources/gamma"} component={GammaRoute} />
      <Route path={"/resources/gamma/8-ways"}>
        <ProtectedRoute component={GammaEightWays} />
      </Route>
      <Route path={"/resources/base44"} component={Base44Route} />
      <Route path={"/resources/base44/8-ways"}>
        <ProtectedRoute component={Base44EightWays} />
      </Route>
      <Route path={"/resources/lovable"} component={LovableRoute} />
      <Route path={"/resources/lovable/8-ways"}>
        <ProtectedRoute component={LovableEightWays} />
      </Route>
      <Route path={"/resources/castmagic"} component={CastmagicRoute} />
      <Route path={"/resources/castmagic/brand-voice"}>
        <ProtectedRoute component={CastmagicBrandVoice} />
      </Route>
      <Route path={"/resources/opus-clip"} component={OpusClipRoute} />
      <Route path={"/resources/opus-clip/cheat-sheet"}>
        <ProtectedRoute component={OpusClipCheatSheet} />
      </Route>
      <Route path={"/resources/agent-opus"} component={AgentOpusRoute} />
      <Route path={"/resources/littlebird"} component={LittlebirdRoute} />
      <Route path={"/resources/littlebird/8-ways"}>
        <ProtectedRoute component={LittlebirdEightWays} />
      </Route>
      <Route path={"/resources/littlebird/scenario-finder"}>
        <ProtectedRoute component={LittlebirdScenarioFinder} />
      </Route>

      {/* Public Littlebird opt-in page, no auth required */}
      <Route path={"/littlebird-quickwins"}>
        <OptinRoute component={LittlebirdOptin} guide="/resources/littlebird/quick-wins" />
      </Route>

      {/* Gated to match /resources/littlebird/8-ways. These are the same kind
          of thing at the same depth in the same path; asking for an account on
          one and not the other is the inconsistency, not the gate. */}
      <Route path={"/resources/littlebird/quick-wins"}>
        <ProtectedRoute component={LittlebirdQuickWinsPage} />
      </Route>

      {/* Gemini Notebook sub-pages */}
      <Route path={"/resources/gemini-notebook/8-ways"}>
        <ProtectedRoute component={NotebookLMEightWays} />
      </Route>
      {/* Public Gemini Notebook opt-in page, no auth required */}
      <Route path={"/gemini-notebook-quickwins"}>
        <OptinRoute component={NotebookLMOptin} guide="/resources/gemini-notebook/quick-wins" />
      </Route>
      {/* Gated to match /resources/gemini-notebook/8-ways. See the note on
          the Littlebird pair above. */}
      <Route path={"/resources/gemini-notebook/quick-wins"}>
        <ProtectedRoute component={NotebookLMQuickWinsPage} />
      </Route>

      {/* Resource Center tool pages */}
      <Route path={"/resources/gohighlevel"} component={GoHighLevelRoute} />

      {/* Renamed 16 Jul 2026, when Google renamed Gemini Notebook to Gemini
          Notebook. The old paths are live on the current Manus deployment and
          are what Google has indexed, so they redirect rather than 404.
          These are client-side redirects, matching the existing pattern below;
          server-side 301s would pass ranking signal more reliably and are the
          better answer if these URLs turn out to have real search traffic. */}
      <Route path={"/resources/notebooklm"}>
        <Redirect to="/resources/gemini-notebook" />
      </Route>
      <Route path={"/resources/notebooklm/8-ways"}>
        <Redirect to="/resources/gemini-notebook/8-ways" />
      </Route>
      <Route path={"/resources/notebooklm/quick-wins"}>
        <Redirect to="/resources/gemini-notebook/quick-wins" />
      </Route>
      <Route path={"/notebooklm-quickwins"}>
        <Redirect to="/gemini-notebook-quickwins" />
      </Route>

      {/* Legacy redirects: keep old paths working */}
      <Route path={"/claude-route"}>
        <Redirect to="/resources/claude" />
      </Route>
      <Route path={"/open-video-route"}>
        <Redirect to="/resources/open-video" />
      </Route>
      <Route path={"/open-video-hooks"}>
        <Redirect to="/resources/open-video/hook-library" />
      </Route>

      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
      >
        <TooltipProvider>
          <AuthModalProvider>
            <Toaster />
            <Router />
            {/* Rendered once, at the root, so opening it never unmounts the
                page underneath. That is what keeps the directory visible
                behind the overlay. */}
            <AuthModal />
          </AuthModalProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
